import { Component, Input, OnDestroy, signal, ViewChild } from "@angular/core";
import DevExpress from "devextreme";
import {
  ButtonType,
  Format,
  TooltipShowMode,
  VerticalEdge,
} from "devextreme/common";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  CanvasService,
  typeConfigKonva,
  TypeConfigKonva,
} from "./canvas-editor/canvas-editor.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CanvasEditorComponent } from "./canvas-editor/canvas-editor.component";
import {
  DxButtonModule,
  DxColorBoxModule,
  DxPopupModule,
  DxSliderModule,
} from "devextreme-angular";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { AsyncPipe, NgIf } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { ModalGalleryService } from "../modal-gallery/modal-gallery.service";
import { DesignKind, DesignService } from "@app/core/services/design.service";
import { DxSharedModule } from "@app/shared/dx-shared.module";
import ValueChangedEventColor = DevExpress.ui.dxColorBox.ValueChangedEvent;
import ValueChangedEventSlider = DevExpress.ui.dxSlider.ValueChangedEvent;

interface SliderTooltipType {
  enabled?: boolean;
  format?: Format | string;
  position?: VerticalEdge;
  showMode?: TooltipShowMode;
}

@Component({
  selector: "ks-editor",
  standalone: true,
  imports: [
    CanvasEditorComponent,
    DxButtonModule,
    FaIconComponent,
    ReactiveFormsModule,
    AsyncPipe,
    DxColorBoxModule,
    DxSliderModule,
    DxPopupModule,
    NgIf,
    TranslateModule,
    DxSharedModule,
  ],
  templateUrl: "./editor.component.html",
  styleUrl: "./editor.component.scss",
})
export class EditorComponent implements OnDestroy {
  @ViewChild(CanvasEditorComponent) canvasEditor!: CanvasEditorComponent;
  @Input({ required: true }) imageUrl: string | null = null;

  public selectedElement$ = this.canvasService.selectedElement$;
  public color$ = this.canvasService.color$;
  public width$ = this.canvasService.width$;
  public image: string;
  protected mode = signal<TypeConfigKonva | null>(null);
  protected readonly sliderTooltip: SliderTooltipType = {
    enabled: true,
    showMode: "onHover",
    position: "bottom",
  };

  protected form = new FormGroup({
    text: new FormControl("", Validators.required),
  });
  protected readonly isTextAccordionOpen = signal<boolean>(false);
  protected readonly showConfirmCancelChangesModal = signal<boolean>(false);
  protected readonly isChangesEditor$ = this.canvasService.getStateIsChanges$();
  protected readonly isLoadedImage$ = this.canvasService.getIsLoadedImage$();
  protected readonly isErrorLoadedImage$ =
    this.canvasService.getErrorIsLoadedImage$();

  protected readonly typeConfigKonva = typeConfigKonva;
  protected design: DesignKind = "classic";

  constructor(
    private canvasService: CanvasService,
    private modalGalleryService: ModalGalleryService,
    private designService: DesignService
  ) {
    this.design = this.designService.getDesign();
    this.designService.design$.pipe(takeUntilDestroyed()).subscribe((d) => {
      this.design = d;
    });
    this.canvasService.mode$.pipe(takeUntilDestroyed()).subscribe((mode) => {
      this.mode.set(mode);
      if (mode) {
        this.isTextAccordionOpen.set(false);
      }
    });

    this.canvasService.selectedElement$
      .pipe(takeUntilDestroyed())
      .subscribe((selectedElement) => {
        if (selectedElement?.type === typeConfigKonva.TEXT) {
          const textSelectedElement = this.canvasService.getTextByName(
            selectedElement.name
          );
          this.form.controls.text.setValue(textSelectedElement.text);
          this.isTextAccordionOpen.set(true);
        } else {
          this.form.controls.text.setValue(null);
          this.isTextAccordionOpen.set(false);
        }
      });
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.modalGalleryService.onCloseEditor();
  }

  protected onCreateRect(): void {
    if (this.mode() === typeConfigKonva.RECT) {
      this.canvasService.setMode(null);
    } else {
      this.canvasService.setMode(typeConfigKonva.RECT);
    }
  }

  protected onCreateArrow(): void {
    if (this.mode() === typeConfigKonva.ARROW) {
      this.canvasService.setMode(null);
    } else {
      this.canvasService.setMode(typeConfigKonva.ARROW);
    }
  }

  protected onClickAccordion(): void {
    this.isTextAccordionOpen.update((prev) => !prev);
    this.canvasService.setMode(null);
  }

  protected onCreateText(): void {
    if (this.selectedElement$.getValue()?.type === typeConfigKonva.TEXT) {
      this.canvasService.updateText({
        name: this.selectedElement$.getValue().name,
        text: this.form.controls.text.value,
      });
    } else {
      this.canvasService.addText(this.form.value.text);
    }

    this.isTextAccordionOpen.set(false);
    this.canvasService.setSelectedElement(null);
    this.form.reset();
  }

  protected onChangeWidth(event: ValueChangedEventSlider): void {
    this.canvasService.setWidth(event.value);
  }

  protected onColorChanged(event: ValueChangedEventColor): void {
    this.canvasService.setColor(event.value);
  }

  protected onDeleteElement(): void {
    this.canvasService.removeElement();
    this.isTextAccordionOpen.set(false);
    this.form.reset();
  }

  protected onCreatePencil(): void {
    if (this.mode() === typeConfigKonva.PENCIL) {
      this.canvasService.setMode(null);
    } else {
      this.canvasService.setMode(typeConfigKonva.PENCIL);
    }
  }

  protected onCreateLine(): void {
    if (this.mode() === typeConfigKonva.LINE) {
      this.canvasService.setMode(null);
    } else {
      this.canvasService.setMode(typeConfigKonva.LINE);
    }
  }

  protected getType(tool: TypeConfigKonva): ButtonType {
    return this.mode() === tool ? "success" : "normal";
  }

  protected onCloseEditor(): void {
    if (this.canvasService.getCheckChangesHaveBeenMade()) {
      this.showConfirmCancelChangesModal.set(true);
    } else {
      this.modalGalleryService.onCloseEditor();
    }
  }

  protected onCancelChanges(): void {
    this.canvasService.allClear();
    this.modalGalleryService.onCloseEditor();
  }

  protected onAllClear(): void {
    this.canvasService.setMode(null);
    this.canvasService.allClear();
  }

  protected onSave() {
    const image = this.canvasEditor.getCanvasAsDataURL();
    this.modalGalleryService.addPhotoToSend(image);
    this.modalGalleryService.onCloseEditor();
    this.onAllClear();
  }
}
